import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnInit,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { toNumber } from '@fgb/core';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'fgbcl-quantity-field',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, FGBSharedModule],
  templateUrl: './quantity-field.component.html',
  styleUrl: './quantity-field.component.scss',
})
export class QuantityFieldComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() minValue: number = 1;
  @Input() maxValue: number = -1;
  @Input() incrementValue: number = 1;
  innerValue: number = 1;
  isDisabled = false;
  control: AbstractControl | any;
  private _onChangeCallback = (_: number) => {};
  private _onTouchedCallback = () => {};
  private destroyRef = inject(DestroyRef);
  @ViewChild('input') inputText: ElementRef<HTMLInputElement>;

  constructor(@Optional() @Self() private ngControl: NgControl) {
    if (this.ngControl) this.ngControl.valueAccessor = this;
  }

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit(): void {
    if (this.ngControl) {
      this.control = this.ngControl.control;
      this.isDisabled = !!this.control?.disabled;
    }
  }

  ngAfterViewInit(): void {
    fromEvent(this.inputText.nativeElement, 'input')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const rawValue = (event.target as HTMLInputElement).value;
        if (rawValue === '') return;
        let value = toNumber(rawValue);
        // If the input value is less than min value, set to the minimum value
        if (value < this.minValue) {
          value = this.minValue;
          (event.target as HTMLInputElement).value = `${value}`;
        }
        // If the input value exceeds the maximum value, set to the maximum value
        if (this.maxValue !== -1 && value > this.maxValue) {
          value = this.maxValue;
          (event.target as HTMLInputElement).value = `${value}`;
        }
        this.emitValueChange(value);
      });

    fromEvent(this.inputText.nativeElement, 'blur')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const rawValue = (event.target as HTMLInputElement).value;
        if (rawValue === '') {
          this.emitValueChange(this.minValue);
        }
        this._onTouchedCallback();
      });
  }

  /* ================================
   * Public API
   * ================================ */

  writeValue(value: number): void {
    this.innerValue = value ?? this.minValue;
  }

  /**
   * Register on change function
   */
  registerOnChange(fn: any): void {
    this._onChangeCallback = fn;
  }

  /**
   * Register on touched function
   */
  registerOnTouched(fn: any): void {
    this._onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /** Prevents using decimal in the number input */
  onNumberKeydown(event: KeyboardEvent): void {
    const allowedControlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    const input = event.target as HTMLInputElement;

    // Prevent decimal
    if (event.key === '.') {
      event.preventDefault();
      return;
    }

    // Allow control keys
    if (allowedControlKeys.includes(event.key)) {
      return;
    }

    // If not a number, prevent and set value to minValue
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      input.value = `${this.minValue}`;
      this.emitValueChange(this.minValue);
    }
  }

  /** Increments the value by the specified incrementValue, ensuring it stays within the defined maximum bounds. */
  increment(): void {
    if (this.isDisabled) return;
    if (this.maxValue === -1 || this.innerValue < this.maxValue) {
      this.emitValueChange(this.innerValue + this.incrementValue);
    }
  }

  /** Decrements the value by the specified incrementValue, ensuring it stays within the defined minimum bounds. */
  decrement(): void {
    if (this.isDisabled) return;

    if (this.innerValue > this.minValue) {
      this.emitValueChange(this.innerValue - this.incrementValue);
    }
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private emitValueChange(value: number): void {
    this.innerValue = value;
    this._onChangeCallback(value);
  }
}
